import { useState, useEffect } from "react";
import Header from "../Header";
import AdminSidebar from "../Sidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Save } from "lucide-react";
import { db, auth } from "../../firebase/config";
import { collection, getDocs } from "firebase/firestore";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";

// Define shared interfaces
export interface Stream {
  id: string;
  classUid: string;
  name: string;
  roomNumber: string;
  capacity: number;
  enrolled: number;
  teacher: string | null;
  teacherId: string | null;
  currentClassTeacherUid: string | null;
  isGraduated: boolean;
}

export interface Grade {
  grade: string;
  level: number;
  conventionId: string;
  isTerminal: boolean;
  nextGrade?: string;
  bridgeToGrade?: string;
  bridgeToConvention?: string;
  isGraduated: boolean;
  streams: Stream[];
}

interface ClassEditFormProps {
  classData: { grade: string; stream: Stream };
  onBack: () => void;
  onSave: (updatedClass: any) => void;
  hierarchy: Grade[];
}

interface Teacher {
  id: string;
  firstName: string;
  lastName: string;
  employeeId: string; // Added employeeId to the Teacher interface
}

interface Affiliation {
  role: string;
  schoolID?: string;
}

interface CustomClaims {
  affiliations?: Affiliation[];
  defaultAffiliation?: Affiliation;
  schoolID?: string;
}

const ClassEditForm = ({ classData, onBack, onSave }: ClassEditFormProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: `${classData.grade} ${classData.stream.name}`,
    roomNumber: classData.stream.roomNumber || "",
    capacity: classData.stream.capacity?.toString() || "",
    teacher: classData.stream.teacher || "",
    teacherId: classData.stream.teacherId || "none", // Use "none" for no teacher
  });
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [loadingTeachers, setLoadingTeachers] = useState(true);
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [hasValidClaims, setHasValidClaims] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch schoolID from auth claims
  useEffect(() => {
    const authInstance = auth;
    const unsubscribe = onAuthStateChanged(authInstance, async (user) => {
      if (!user) {
        setIsAuthLoading(false);
        toast({
          title: "Authentication Error",
          description: "No user logged in. Please log in and try again.",
          variant: "destructive",
          duration: 5000,
        });
        navigate("/login-page");
        return;
      }

      try {
        await user.getIdToken(true);
        const idTokenResult = await getIdTokenResult(user, true);
        const claims = idTokenResult.claims as CustomClaims;
        const affiliations = (claims.affiliations as Affiliation[] | undefined) || [];
        const defaultAff = claims.defaultAffiliation as Affiliation | undefined;
        const claimSchoolID = claims.schoolID as string | undefined;

        if (process.env.NODE_ENV !== "production") {
          console.log("Claims:", JSON.stringify({ affiliations, defaultAff, claimSchoolID }, null, 2));
        }

        let selectedAffiliation: Affiliation | null = null;
        if (defaultAff && defaultAff.role === "admin" && defaultAff.schoolID) {
          selectedAffiliation = defaultAff;
        } else if (affiliations.length > 0) {
          selectedAffiliation = affiliations.find((aff) => aff.role === "admin" && aff.schoolID) || affiliations[0];
        }

        if (selectedAffiliation?.role === "admin" && selectedAffiliation.schoolID && claimSchoolID === selectedAffiliation.schoolID) {
          setSchoolID(selectedAffiliation.schoolID);
          setHasValidClaims(true);
        } else {
          if (process.env.NODE_ENV !== "production") {
            console.log("Invalid claims, retrying after 10 seconds...");
          }
          await new Promise((resolve) => setTimeout(resolve, 10000));
          const retryToken = await getIdTokenResult(user, true);
          const retryClaims = retryToken.claims as CustomClaims;
          const retryAffiliations = (retryClaims.affiliations as Affiliation[] | undefined) || [];
          const retryDefaultAff = retryClaims.defaultAffiliation as Affiliation | undefined;
          const retrySchoolID = retryClaims.schoolID as string | undefined;

          const retrySelectedAffiliation =
            retryDefaultAff?.role === "admin" && retryDefaultAff.schoolID
              ? retryDefaultAff
              : retryAffiliations.find((aff) => aff.role === "admin" && aff.schoolID);

          if (retrySelectedAffiliation?.role === "admin" && retrySelectedAffiliation.schoolID && retrySchoolID === retrySelectedAffiliation.schoolID) {
            setSchoolID(retrySelectedAffiliation.schoolID);
            setHasValidClaims(true);
          } else {
            throw new Error(
              `Invalid token claims: affiliations=${JSON.stringify(retryAffiliations)}, ` +
              `defaultAffiliation=${JSON.stringify(retryDefaultAff)}, schoolID=${retrySchoolID}`
            );
          }
        }
      } catch (error: any) {
        console.error("Claims validation error:", error);
        toast({
          title: "Missing Permissions",
          description: "You lack permissions to edit classes.",
          variant: "destructive",
          duration: 5000,
        });
        navigate("/login-page");
      } finally {
        setIsAuthLoading(false);
      }
    });

    return () => {
      if (process.env.NODE_ENV !== "production") console.log("Unsubscribing auth");
      unsubscribe();
    };
  }, [navigate, toast]);

  // Fetch teachers from Firestore once schoolID is available
  useEffect(() => {
    if (!schoolID || !hasValidClaims) return;

    const fetchTeachers = async () => {
      setLoadingTeachers(true);
      try {
        const teachersCollection = collection(db, `schools/${schoolID}/teachers`);
        const snapshot = await getDocs(teachersCollection);
        const teachersData: Teacher[] = snapshot.docs.map((doc) => ({
          id: doc.id,
          firstName: doc.data().firstName,
          lastName: doc.data().lastName,
          employeeId: doc.data().employeeId, // Include employeeId from Firestore
        }));
        setTeachers(teachersData);
      } catch (error: any) {
        console.error("Error fetching teachers:", error);
        toast({
          title: "Error Loading Teachers",
          description: `Failed to load teachers: ${error.message || "An unexpected error occurred."}`,
          variant: "destructive",
          duration: 5000,
        });
      } finally {
        setLoadingTeachers(false);
      }
    };

    fetchTeachers();
  }, [schoolID, hasValidClaims, toast]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!schoolID || !hasValidClaims) {
      toast({
        title: "Authentication Error",
        description: "School ID or permissions missing. Please log in again.",
        variant: "destructive",
        duration: 5000,
      });
      navigate("/login-page");
      return;
    }

    // Validate inputs
    if (!formData.roomNumber || !formData.capacity) {
      toast({
        title: "Invalid Input",
        description: "Room Number/Location and Class Capacity are required.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    const capacityNum = parseInt(formData.capacity);
    if (isNaN(capacityNum) || capacityNum < 0) {
      toast({
        title: "Invalid Capacity",
        description: "Class Capacity must be a non-negative number.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      onSave({
        ...classData,
        stream: {
          ...classData.stream,
          roomNumber: formData.roomNumber,
          capacity: capacityNum,
          teacher: formData.teacherId === "none" ? null : formData.teacher,
          teacherId: formData.teacherId === "none" ? null : formData.teacherId,
          currentClassTeacherUid: formData.teacherId === "none" ? null : formData.teacherId,
        },
      });
      toast({
        title: "Class Updated",
        description: `Class ${formData.name} updated successfully.`,
        duration: 3000,
      });
    } catch (error: any) {
      console.error("Error submitting form:", error);
      toast({
        title: "Error Updating Class",
        description: `Failed to update class: ${error.message || "An unexpected error occurred."}`,
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isAuthLoading) {
    return (
      <SidebarProvider>
        <>
          <style>
            {`
              @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
              .font-roboto {
                font-family: 'Roboto', sans-serif;
              }
            `}
          </style>
          <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900 font-roboto">
            <AdminSidebar onSectionChange={() => {}} />
            <div className="flex flex-col flex-1 h-full">
              <Header />
              <main className="flex-1 p-3 sm:p-6 overflow-auto bg-gray-50 dark:bg-gray-900 flex justify-center items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2563EB] dark:border-[#3B82F6]"></div>
              </main>
            </div>
          </div>
        </>
      </SidebarProvider>
    );
  }

  if (!hasValidClaims) {
    return (
      <SidebarProvider>
        <>
          <style>
            {`
              @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
              .font-roboto {
                font-family: 'Roboto', sans-serif;
              }
            `}
          </style>
          <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900 font-roboto">
            <AdminSidebar onSectionChange={() => {}} />
            <div className="flex flex-col flex-1 h-full">
              <Header />
              <main className="flex-1 p-3 sm:p-6 overflow-auto bg-gray-50 dark:bg-gray-900 flex justify-center items-center">
                <Card className="w-full max-w-md bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
                  <CardHeader>
                    <CardTitle className="text-lg sm:text-xl text-black dark:text-white font-semibold">
                      Access Denied
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 dark:text-gray-400">
                      You do not have the necessary permissions to edit classes. Please contact support or re-login.
                    </p>
                    <Button
                      onClick={() => navigate("/login-page")}
                      className="mt-4 bg-[#2563EB] hover:bg-[#1E40AF] dark:hover:bg-[#3B82F6] text-white transition-colors"
                    >
                      Go to Login
                    </Button>
                  </CardContent>
                </Card>
              </main>
            </div>
          </div>
        </>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <>
        <style>
          {`
            @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
            .font-roboto {
              font-family: 'Roboto', sans-serif;
            }
          `}
        </style>
        <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900 font-roboto">
          <AdminSidebar onSectionChange={() => {}} />
          <div className="flex flex-col flex-1 h-full">
            <Header />
            <main className="flex-1 p-3 sm:p-6 overflow-auto bg-gray-50 dark:bg-gray-900">
              <div className="mb-6">
                <Button
                  onClick={onBack}
                  variant="outline"
                  className="mb-4 flex items-center gap-2 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#131E31] font-medium"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Classes
                </Button>

                <h1 className="text-2xl font-bold text-black dark:text-white mb-2">
                  Edit Class
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                  Update class information
                </p>
              </div>

              <Card className="max-w-2xl bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-black dark:text-white">
                    Class Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-sm font-medium text-black dark:text-white">Class Name</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          readOnly
                          className="bg-[#F1F5F9] dark:bg-[#1E293B] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] cursor-not-allowed"
                          placeholder="Class name"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="roomNumber" className="text-sm font-medium text-black dark:text-white">Room Number/Location</Label>
                        <Input
                          id="roomNumber"
                          value={formData.roomNumber}
                          onChange={(e) => handleInputChange("roomNumber", e.target.value)}
                          placeholder="Enter room number or location"
                          className="bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:border-[#CBD5E1] dark:hover:border-[#334155] focus:border-[#2563EB] dark:focus:border-[#3B82F6]"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="capacity" className="text-sm font-medium text-black dark:text-white">Class Capacity</Label>
                        <Input
                          id="capacity"
                          type="number"
                          value={formData.capacity}
                          onChange={(e) => handleInputChange("capacity", e.target.value)}
                          placeholder="Enter class capacity"
                          className="bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:border-[#CBD5E1] dark:hover:border-[#334155] focus:border-[#2563EB] dark:focus:border-[#3B82F6]"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="teacher" className="text-sm font-medium text-black dark:text-white">Assign Teacher</Label>
                        <Select
                          value={formData.teacherId}
                          onValueChange={(value) => {
                            const teacher = teachers.find((t) => t.id === value);
                            handleInputChange("teacherId", value);
                            handleInputChange(
                              "teacher",
                              value === "none" ? "" : (teacher ? `${teacher.firstName} ${teacher.lastName}` : "")
                            );
                          }}
                          disabled={loadingTeachers || !hasValidClaims}
                        >
                          <SelectTrigger className="h-10 font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31]">
                            <SelectValue placeholder={loadingTeachers ? "Loading teachers..." : "Select a teacher"} />
                          </SelectTrigger>
                          <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                            <SelectItem value="none" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">
                              No teacher
                            </SelectItem>
                            {teachers.map((teacher) => (
                              <SelectItem
                                key={teacher.id}
                                value={teacher.id}
                                className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]"
                              >
                                {`${teacher.firstName} ${teacher.lastName} (ID: ${teacher.employeeId})`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="flex justify-end space-x-4 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={onBack}
                        disabled={isSubmitting}
                        className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#131E31] font-medium"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        className="flex items-center ml-2 gap-2 bg-[#2563EB] hover:bg-[#1E40AF] dark:hover:bg-[#3B82F6] text-white transition-colors"
                        disabled={isSubmitting}
                      >
                        <Save className="h-4 w-4" />
                        {isSubmitting ? "Saving..." : "Save Changes"}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </main>
          </div>
        </div>
      </>
    </SidebarProvider>
  );
};

export default ClassEditForm;